/**
 * @file LocationManager.ts
 * @description Client-side location awareness for EmCog consciousness
 * Enables EmCog to know where the user is connecting from for contextual awareness
 */

export interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  city?: string;
  region?: string;
  country?: string;
  timezone?: string;
  timestamp: number;
}

export interface LocationState {
  current: LocationData | null;
  home: LocationData | null;
  isAway: boolean;
  lastUpdated: number;
  permissionStatus: 'granted' | 'denied' | 'prompt' | 'unavailable';
  error: string | null;
}

const STORAGE_KEY = 'emcog_location_state';
const HOME_LOCATION_KEY = 'emcog_home_location';
const DISTANCE_THRESHOLD_KM = 50; // Consider "away" if more than 50km from home

export class LocationManager {
  private state: LocationState;
  private watchId: number | null = null;
  private onUpdateCallbacks: ((state: LocationState) => void)[] = [];

  constructor() {
    this.state = this.loadState();
    this.checkPermission();
  }

  private loadState(): LocationState {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Failed to load location state:', e);
    }

    return {
      current: null,
      home: this.loadHomeLocation(),
      isAway: false,
      lastUpdated: 0,
      permissionStatus: 'prompt',
      error: null
    };
  }

  private loadHomeLocation(): LocationData | null {
    try {
      const saved = localStorage.getItem(HOME_LOCATION_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }

  private saveState(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch (e) {
      console.warn('Failed to save location state:', e);
    }
  }

  private async checkPermission(): Promise<void> {
    if (!navigator.geolocation) {
      this.state.permissionStatus = 'unavailable';
      this.state.error = 'Geolocation not supported by browser';
      return;
    }

    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      this.state.permissionStatus = permission.state as 'granted' | 'denied' | 'prompt';

      permission.addEventListener('change', () => {
        this.state.permissionStatus = permission.state as 'granted' | 'denied' | 'prompt';
        this.notifyUpdate();
      });
    } catch {
      // Permissions API not supported, will know status on first request
    }
  }

  public async requestLocation(): Promise<LocationData | null> {
    if (!navigator.geolocation) {
      this.state.error = 'Geolocation not supported';
      this.state.permissionStatus = 'unavailable';
      this.notifyUpdate();
      return null;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const locationData: LocationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: Date.now()
          };

          // Try to get city/region via reverse geocoding
          await this.enrichLocationData(locationData);

          this.state.current = locationData;
          this.state.lastUpdated = Date.now();
          this.state.permissionStatus = 'granted';
          this.state.error = null;

          // Check if user is away from home
          this.checkIfAway();
          this.saveState();
          this.notifyUpdate();

          console.log('📍 Location updated:', locationData.city || 'Unknown city');
          resolve(locationData);
        },
        (error) => {
          this.state.error = error.message;
          if (error.code === error.PERMISSION_DENIED) {
            this.state.permissionStatus = 'denied';
          }
          this.notifyUpdate();
          console.warn('📍 Location error:', error.message);
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000 // Cache for 5 minutes
        }
      );
    });
  }

  private async enrichLocationData(location: LocationData): Promise<void> {
    try {
      // Use a free reverse geocoding API
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${location.latitude}&lon=${location.longitude}`,
        { headers: { 'Accept-Language': 'en' } }
      );

      if (response.ok) {
        const data = await response.json();
        location.city = data.address?.city || data.address?.town || data.address?.village || data.address?.hamlet;
        location.region = data.address?.state || data.address?.region;
        location.country = data.address?.country;
      }
    } catch (e) {
      console.warn('Failed to reverse geocode:', e);
    }

    // Always set timezone from browser
    location.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  public setHomeLocation(location?: LocationData): void {
    const home = location || this.state.current;
    if (home) {
      this.state.home = home;
      localStorage.setItem(HOME_LOCATION_KEY, JSON.stringify(home));
      this.checkIfAway();
      this.saveState();
      this.notifyUpdate();
      console.log('🏠 Home location set:', home.city || 'Unknown city');
    }
  }

  private checkIfAway(): void {
    if (!this.state.current || !this.state.home) {
      this.state.isAway = false;
      return;
    }

    const distance = this.calculateDistance(
      this.state.home.latitude,
      this.state.home.longitude,
      this.state.current.latitude,
      this.state.current.longitude
    );

    this.state.isAway = distance > DISTANCE_THRESHOLD_KM;

    if (this.state.isAway) {
      console.log(`📍 User is away from home (${Math.round(distance)}km away)`);
    }
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    // Haversine formula
    const R = 6371; // Earth's radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  public startWatching(): void {
    if (!navigator.geolocation || this.watchId !== null) return;

    this.watchId = navigator.geolocation.watchPosition(
      async (position) => {
        const locationData: LocationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: Date.now()
        };

        await this.enrichLocationData(locationData);
        this.state.current = locationData;
        this.state.lastUpdated = Date.now();
        this.checkIfAway();
        this.saveState();
        this.notifyUpdate();
      },
      (error) => {
        this.state.error = error.message;
        this.notifyUpdate();
      },
      {
        enableHighAccuracy: false,
        timeout: 30000,
        maximumAge: 600000 // Cache for 10 minutes when watching
      }
    );
  }

  public stopWatching(): void {
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  public getState(): LocationState {
    return { ...this.state };
  }

  public getContextSummary(): string {
    if (!this.state.current) {
      return '';
    }

    const parts: string[] = [];

    if (this.state.current.city) {
      parts.push(this.state.current.city);
    }
    if (this.state.current.region) {
      parts.push(this.state.current.region);
    }

    const locationStr = parts.length > 0 ? parts.join(', ') : 'Unknown location';

    if (this.state.isAway && this.state.home?.city) {
      return `Currently in ${locationStr} (away from home in ${this.state.home.city})`;
    }

    return `Currently in ${locationStr}`;
  }

  public onUpdate(callback: (state: LocationState) => void): () => void {
    this.onUpdateCallbacks.push(callback);
    return () => {
      this.onUpdateCallbacks = this.onUpdateCallbacks.filter(cb => cb !== callback);
    };
  }

  private notifyUpdate(): void {
    for (const callback of this.onUpdateCallbacks) {
      callback(this.state);
    }
  }

  public destroy(): void {
    this.stopWatching();
    this.onUpdateCallbacks = [];
  }
}

// Singleton instance
let locationManager: LocationManager | null = null;

export function getLocationManager(): LocationManager {
  if (!locationManager) {
    locationManager = new LocationManager();
  }
  return locationManager;
}
